import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import {
    FormControl,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { FileItem, FileLikeObject, FileUploader, FileUploaderOptions } from 'ng2-file-upload';
import { NgxSpinnerService } from 'ngx-spinner';
import { combineLatest, lastValueFrom } from 'rxjs';
import { User } from 'src/app/core/models/user';
import { MemoryStorageService } from 'src/app/core/services/memory-storage.service';
import { UserService } from 'src/app/core/services/user.service';
import swal from 'sweetalert2';
import banks from '../../../../assets/data/FinbitBankList.json';
import { environment } from '../../../../environments/environment';
import { AccountService } from '../../../core/services/account.service';
import { SessionTimerService } from '../../../core/services/session-timer.service';

const toBase64 = (file: File) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });

// TODO: remove below, and use existing validations
function fileValidation(files: FileItem[]) {
    // const sizeExceed = files.filter((file) => file._file.size >= 2048000);
    const sizeExceed = files.filter((file) => file._file.size >= 10000000);
    // file size
    if (sizeExceed.length) {
        swal.fire('Size exceed');
        return false;
    }
    // file type
    const invalidFiles = files.filter(
        (file) => !['application/pdf'].includes(file._file.type)
    );
    if (invalidFiles.length) {
        swal.fire('Invalid File Type');
        return false;
    }
    return true;
}

@Component({
    selector: 'app-bank-statement',
    templateUrl: './bank-statement.component.html',
    styleUrls: ['./bank-statement.component.scss'],
})
export class BankStatementComponent implements OnInit {
    public maxFileCount: number = 13;

    public requestCode: string = '5011';

    public docType: string = 'BANK STATEMENT';

    public subDocType: string = 'bankstatement';

    public allowedMimeType = ['application/pdf'];

    public image!: any;

    public lastSixMonthDateMsg: string;

    public uploadedBy: string = "1";

    @Output()
    public apiResponse = new EventEmitter();

    public showAccountError: boolean = false;

    public applicationId: number | null = null;

    public uploaderOptions: FileUploaderOptions = {
        url: 'https://mihuru.com',
        queueLimit: this.maxFileCount,
        disableMultipart: true,
        formatDataFunctionIsAsync: true,
        formatDataFunction: async (item: FileItem) => {
            if (item.file.type === 'application/pdf') {
                return {
                    name: item.file.name,
                    length: item.file.size,
                    contentType: item.file.type,
                    date: new Date(),
                };
            }
        },
        filters: [
            {
                name: 'imageAndPdfFilter',
                fn: (item: FileLikeObject) => {
                    return item.type === 'application/pdf';
                }
            }
        ]
    };

    public uploader = new FileUploader(this.uploaderOptions);

    public hasBaseDropZoneOver: boolean;

    public hasAnotherDropZoneOver: boolean;

    public response: string;

    public bankList: { bankName: string; bankCode: string }[] = [];

    public accountTypeList: { Name: string; Code: string }[] = [{ Name: 'Saving', Code: 'SAVING' }, { Name: 'Current', Code: 'CURRENT' }];

    public baseUrl: string = '';

    public bsForm: UntypedFormGroup;

    constructor(
        private router: Router,
        private formBuilder: UntypedFormBuilder,
        private http: HttpClient,
        private sanitizer: DomSanitizer,
        private accountService: AccountService,
        private spinner: NgxSpinnerService,
        private sessionTimerService: SessionTimerService,
        private route: ActivatedRoute,
        private userService: UserService) {
        this.hasBaseDropZoneOver = false;
        this.hasAnotherDropZoneOver = false;

        this.response = '';

        this.uploader.response.subscribe((res: any) => (this.response = res));
        this.baseUrl = environment.apiUrl + 'assistmodule/v1/createUserProfile';
        this.bsForm = formBuilder.group({
            bankName: new FormControl({ value: this.bankList, disabled: false }, Validators.required),
            bankAccountType: new FormControl({ value: this.accountTypeList, disabled: true }, [Validators.required]),
            bankPassword: new FormControl({ value: '', disabled: true }),
            accountNumber: new FormControl({ value: '', disabled: true }, [Validators.required, Validators.maxLength(10)])
        });
    }

    public isNextDisabled: boolean = true;

    public ngOnInit(): void {
        this.spinner.hide();
        //this.sessionTimerService.resumeTimer();
        this.bsForm = this.formBuilder.group({
            bankName: new FormControl({ value: '', disabled: false }, Validators.required),
            bankAccountType: new FormControl({ value: '', disabled: true }, [Validators.required]),
            bankPassword: new FormControl({ value: '', disabled: true }),
            accountNumber: new FormControl({ value: '', disabled: true }, [Validators.required, Validators.maxLength(10)])
        });

        const bankNameControl = this.bsForm.get('bankName');
        const accountNumberControl = this.bsForm.get('accountNumber');
        const bankAccountTypeControl = this.bsForm.get('bankAccountType');

        // Enable Account Type field when Bank name is filled.
        bankNameControl.valueChanges.subscribe(value => {
            const bankAccountType = this.bsForm.get('bankAccountType');
            if (value) {
                bankAccountType?.enable();
            } else {
                bankAccountType?.disable();
                this.bsForm.get('bankPassword')?.disable();
                this.bsForm.get('accountNumber')?.disable();
            }
        })

        // Enable Account Number field when Account Type is Selected.
        bankAccountTypeControl.valueChanges.subscribe(value => {
            const accountNumberControl = this.bsForm.get('accountNumber');
            if (value) {
                accountNumberControl.enable();
            } else {
                this.bsForm.get('accountNumber')?.disable();
                this.bsForm.get('bankPassword')?.disable();
            }
        })

        accountNumberControl.valueChanges.subscribe(value => {
            const statementPassword = this.bsForm.get('bankPassword');

            if (value) {
                statementPassword.enable();
            } else {
                statementPassword.disable();
            }
        })

        if (bankNameControl && accountNumberControl && bankAccountTypeControl) {
            combineLatest([
                bankNameControl.valueChanges,
                accountNumberControl.valueChanges,
                bankAccountTypeControl.valueChanges
            ]).subscribe(([bankName, accountNumber, bankAccountType]) => {

                const isAnyEmpty =
                    !bankName || bankName.length === 0 ||
                    !accountNumber || accountNumber.length === 0 ||
                    !bankAccountType || bankAccountType.length === 0;

                this.isNextDisabled = isAnyEmpty;
            });
        }

        this.setStatementDate();
        //removing monedo supported nach bank list
        //this.getBankListFromNuPay();
        //adding all the digitap supported bank list 
        this.bankList = banks.data;


        this.route.paramMap.subscribe(params => {
            this.applicationId = +params.get('applicationId')!;
        });

        if (this.applicationId && this.applicationId != 0) {
            this.spinner.show();
            MemoryStorageService.removeItem("customerToken");
            this.accountService.clearCurrentUser();
            lastValueFrom(this.userService.getApplicationTokenByApplicationId(this.applicationId)).then(token => {
                console.log('Agent details:', token);
                lastValueFrom(this.userService.getUserPlanDetailsforBSUpload(this.applicationId, token))
                    .then((response: any) => {

                        var user: User = {
                            userName: response.data.email,
                            email: response.data.email,
                            mobileNumber: response.data.mobileNumber,
                            token: token,
                            fullname: response.data.firstname + ' ' + response.data.lastName,
                            docs: [
                                {
                                    bankstatement: true,
                                },
                            ],
                        };
                        this.accountService.setCurrentUser(user);

                        MemoryStorageService.setItem("customerToken", token);

                        MemoryStorageService.setItem('user', JSON.stringify(user));
                        MemoryStorageService.setItem('uploadedBy', "99");
                        this.spinner.hide();

                        if (response.data.status === "Professional Details not found") {
                            // Used for LOS Bankstatement upload when professional details not found to open professionaldetails page
                            MemoryStorageService.setItem("ProfessionalDetailsUploadPage", 'true');
                            MemoryStorageService.setItem('uploadedBy', "99");
                            //
                            swal.fire({
                                text: 'Professional Details not found!',
                                confirmButtonText: 'OK'
                            }).then((result) => {
                                return this.router.navigateByUrl('/assist-journey/professional-details');
                            });
                        }
                    }).catch((error: any) => {
                        this.spinner.hide();
                        swal.fire({
                            text: '' + error.errors,
                            confirmButtonText: 'OK'
                        }).then((result) => {
                            return this.router.navigateByUrl('/assist-journey/sorry');
                        });
                        console.error('Error fetching ApplicationToken', error);
                        if (error instanceof HttpErrorResponse) {
                            console.error('Error status:', error.status);
                            console.error('Error message:', error.message);
                        }
                    });
            }).catch((error: any) => {
                this.spinner.hide();
                swal.fire({
                    text: 'Application not found!',
                    confirmButtonText: 'OK'
                }).then((result) => {
                    return this.router.navigateByUrl('/assist-journey/sorry');
                });
                console.error('Error fetching ApplicationToken', error);
                if (error instanceof HttpErrorResponse) {
                    console.error('Error status:', error.status);
                    console.error('Error message:', error.message);
                }
            });
        }
    }

    // Returns an observable
    public async upload() {
        this.spinner.show();
        const files = this.uploader.queue;
        if (!fileValidation(files)) {
            this.spinner.hide();
            return;
        }

        const fileBase64 = await Promise.all(
            files.map((file) => toBase64(file._file))
        );

        this.uploadedBy = MemoryStorageService.getItem('uploadedBy')! === "99" ? "99" : "1";

        const selectedBank = this.bsForm.get('bankName')?.value;
        const bankType = this.bsForm.get('bankAccountType')?.value
        const body = {
            uploadedBy: this.uploadedBy,
            requestCode: this.requestCode,
            docType: this.docType,
            subDocType: this.subDocType,
            // TODO: send file object instead
            fileType: files[0]._file.type == 'application/pdf' ? 'pdf' : 'image',
            fileName: files[0]._file.name,
            files: fileBase64,
            bankName: selectedBank.bankName,
            bankCode: parseInt(selectedBank.bankCode),
            bankAccountType: bankType.Code,
            bankPassword: this.bsForm.get('bankPassword')?.value,
            accountNumber: this.bsForm.get('accountNumber')?.value
        };

        return this.http.post(this.baseUrl, body).subscribe({
            next: (response: any) => {
                this.spinner.hide();
                this.HandleBankStatementResponse(response);
            },
        });
    }

    public HandleBankStatementResponse(response: any) {
        // for testing only
        // if(MemoryStorageService.getItem("mobile") === "9990022401"){
        //   return this.router.navigateByUrl('/assist-journey/assist-payment-plan');
        // }
        ////
        this.apiResponse.emit(response);
        this.updatePreview();
        if (response['data'] != null) {
            if (response['data'] && response['data'].hasOwnProperty('response_summary') && (response['data']['response_summary'] == 'Case is approved with credit line' || response['data']['response_summary'] == 'User is approved')) {
                var user = JSON.parse(MemoryStorageService.getItem('user')!);
                user.inPrincipleAmount = response['data']['ipa'];
                this.accountService.setCurrentUser(user);
                MemoryStorageService.setItem('user', JSON.stringify(user));
                // swal.fire('Bank Statment was successfully uploaded.');
                if (this.applicationId && this.applicationId != 0) {
                    return;
                }
                this.router.navigateByUrl('/assist-journey/assist-payment-plan');
            }
        } else {
            if (response['error'] != null) {

                if (response['error']['response_summary'] == "Please enter correct PDF password.") {
                    swal.fire(response['error']['response_summary'] + '!');
                    this.bsForm.get('bankPassword').reset();
                    return;
                }

                this.uploader.clearQueue();
                this.bsForm.reset();

                if (response['error'] && response['error'].hasOwnProperty('response_msg') && (response['error']['response_msg'].includes("Bank statement is incomplete") || response['error']['response_msg'].includes("Bank statement is not recent"))) {
                    swal.fire(response['error']['response_msg']);
                    return;
                }

                if (response['error']['response_summary'] == 'Bank statement verification failed' && response['error']['response_msg'] == 'Three wrong tries') {
                    this.router.navigateByUrl('/assist-journey/assist-underwrite');
                }
                else if (response['error']['response_summary'] == 'Unable to identify your salary details') {
                    swal.fire('Upload latest salary slip in next step');
                    this.router.navigateByUrl('/assist-journey/salary-upload');
                }
                else if (response['error']['response_summary'] == 'Customer is denied') {
                    swal.fire(response['error']['response_msg']);
                    this.router.navigateByUrl('/assist-journey/sorry');
                }
                else if (response['error']['response_summary'] == 'User is declined') {
                    swal.fire(response['error']['response_msg']);
                    this.router.navigateByUrl('/assist-journey/sorry');
                }
                else if (
                    response['error']['response_msg'] ==
                    'The Bank Statement File is failed to read. Please Upload Again'
                ) {
                    swal.fire(['error']['response_summary'] + ' Please Upload Again');
                }
                else if (response['error']['response_summary'] == 'User sent for manual approval') {
                    swal.fire('Bank Statment was successfully uploaded.');
                    return this.router.navigateByUrl('/assist-journey/assist-current-address-proof');
                }
                else {
                    swal.fire('Bank statement upload failed, please try again!');
                    //this.router.navigateByUrl('/assist-journey/assist-underwrite');-
                }
            } else {
                swal.fire('Bank statement upload failed, please try again!');
                //this.router.navigateByUrl('/assist-journey/assist-underwrite');
            }
        }
    }

    public updatePreview() {
        const files = this.uploader.queue;
        if (files.length == 0) {
            swal.fire('Only PDFs are allowed');
        }
        fileValidation(files);
        this.image = files.length ? this.sanitizer.bypassSecurityTrustResourceUrl(
            URL.createObjectURL(files[0]._file)
        ) : null;
    }


    public onFileSelected(event) {
        const files = event.target.files;
        console.log(files, "list of files");
    }

    public fileOverBase(e: any): void {
        this.hasBaseDropZoneOver = e;
    }

    public fileOverAnother(e: any): void {
        this.hasAnotherDropZoneOver = e;
    }

    public get m() {
        return this.bsForm.controls;
    }

    public checkAccountNumber(event: Event): void {
        const inputElement = event.target as HTMLInputElement;
        const value = inputElement.value;
        if (value === '' || value.length > 20) {
            this.showAccountError = true;
            return;
        }
        this.showAccountError = false;
    }

    public removeFile(item: FileItem, event: MouseEvent): void {
        event.stopPropagation();
        if (item && this.uploader.queue.includes(item)) {
            this.uploader.removeFromQueue(item);
            if (this.uploader.queue.length > 0) {
                this.updatePreview();
            } else {
                this.image = false;
            }
        }
    }

    public setStatementDate() {
        const today = new Date();

        // ➤ End date = today
        const endDate = this.formatDate(today);

        // ➤ Start date = first day of the month, 6 months earlier
        const start = new Date(today.getFullYear(), today.getMonth() - 6, 1);
        const startDate = this.formatDate(start);

        this.lastSixMonthDateMsg = `Statement starting from ${startDate} till ${endDate}`;
    }

    private formatDate(d: Date): string {
        const dd = d.getDate().toString().padStart(2, '0');
        const mm = (d.getMonth() + 1).toString().padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}-${mm}-${yyyy}`;
    }


    public blockDash(event: KeyboardEvent): void {
        if (event.key === '-') {
            event.preventDefault();
        }
    }

    private getBankListFromNuPay() {
        this.accountService.getBankListNuPay().subscribe({
            next: (response: any) => {
                const { data } = response;
                const nuPayBanks = new Set(data.map(nuPaBank => nuPaBank.name.toLowerCase()))
                const validBanks = banks.data.filter(bank => nuPayBanks.has(bank.bankName.toLowerCase()));
                this.bankList = validBanks;
            },
            error: (error) => {
                console.log(error, "error on Bank List from NuPay")
            }
        })
    }
}
// 1. base64 is having file type in starting - how to handle that in c# ------??? done
// 2. how to pass values of other objects ----- seperate input fields
// 3. how to get response to parrent controller -----??? -- navigated from parent component
// 4. in case of non image where we are unable to perview dispolay file name -- Done

//{"error":null,"data":{"response_code":"20017","response_summary":"Case is approved with credit line","plan_details":[{"plan_id":"PBYF","plan_name":"Pay before you fly","plan_eligibility":true,"package_cost":50000.0,"loan_amount":34000.00,"downpayment":16000.0,"processing_fee":680.0,"upfrontAmountPayDate":"2023-04-04T18:32:54.0755587+05:30","emi_amount":17000.0,"emi_date":"2023-05-05T00:00:00","tenor":2.0,"roi":0.00,"gst":123.0,"nach_frequency":"MONTHLY","status":"SUCCESS","message":null,"eligibility":null,"emiPlan":"Splifare","navigate_url":"https://los.mihuru.com/travelplan?planId=4a0ee0f2-8356-4f36-ac4b-2ea9d0a95f37","qr_code":"data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAABkAQMAAABKLAcXAAAABlBMVEX///8AAABVwtN+AAAACXBIWXMAAA7EAAAOxAGVKw4bAAADuUlEQVQ4jWXT70sbdxwH8E+MzUWSJssGwyzpnZC0PtlQO2gzL503Il6ksDwVFHL+oO2gqz8K9cQsd7GgEyWxDqZQagb2D2gpjJTJkjTBU2bV7ZFM11w8wT2wmmWjns31bteHfv0+ez35fj7fN+8vwKnDZWzMcOGZmqdxTgTqazd769JoORNiKAbCbY0w75x6lIOZ8BmF8PCNHe68kq16r6/qvA+EyqAyMXRGVOjDm4PNlsEWbkC/k1v97PGPf17/1vvzrD4PsKwcbbbedtvV98ucEjdSR+JYxer/9bvHPJBfHEa+uZi3iusLYVQRWtqBlpULBGVfZAASDol5a+6BjWgvIGql/2iZaG59JAa1JYCbhpdbH3umDFuNAz4eqrO57bl9OepfdwiAyM8fuv42CDh2zO0zgG2sB46FlUgu1J/jEXVebVoi3vpfS6HYhAiBlYM+p/bLgrTcM8qD317qYi5hqsQa0qg65M16U67E7LWV+3jwPnlFzQkpozA+4BMRwYjC8idTEX/TnQAA3fgyebJVrWAT0VYeUfj4MquaRo9y7bieEuFzSLV7mKPy5SHwYAx6UsPkhkOjYxMMIl8qqyS3l3a3uFIdA4H1vx7ekKlpT2YmAohaU578eJ69k0/3sAyQ2U2y+5V5trJMZERE9ZNaRTU8c7ufuzkGelWRnJNkiUzYZX3PmgPGsWsvylfGCAYR0d5QcXwSxIVouVN/n63hp6MXFgIbsREios6TjSRfUKx1QbsmQtdTvO8kJ4/lqeJ9EYyZBqHbZNE07fw8IPJcUYAVIwuYqezR7wyt9daC1e6dvJsERITvMttd/Xkvxrk5PcHlDAXkfwR2L5YTEdGTam+/K3ME2uuUXuvhd4tFJ1+GdaABOnxqR+07edd9XOQZRESmWPinwO9K5hkPD67na7Ul15qqDHNpVGR2J+DI/na7UDPdoXeX/9Q/mM3/K9XEMVSee4X9751yKace9AFE7Bl37Nz0hQpr1HsdoTexuJAe3GtjOFR9jR/dL8pUTBTiAREYmpCTy1jM/ZR4yCDqurrjkkmlXM336M1yh3BWNk4fEIkBQk8p7lh0XpRnK3RCRNW53KK3Lv2BKwhdAFTTtdyDc0t3QU9CREQP19WL25WShzUq+p82G+fHTdaqPU11iYi4VRuJ49erXPEhPReqzSi/aTYTijCk7xIOZXzRbXlXsyUqZ+URDcSTMXgTxwDC7S/YW3kL3hL/IYmKCv7Oz11T4hLXrw/nVrMFLm/hvVFinkd06vwPlaVVuPxFT18AAAAASUVORK5CYII="},{"plan_id":"BP","plan_name":"Bullet payment","plan_eligibility":false,"package_cost":0.0,"loan_amount":0.0,"downpayment":0.0,"processing_fee":0.0,"upfrontAmountPayDate":"0001-01-01T00:00:00","emi_amount":0.0,"emi_date":"0001-01-01T00:00:00","tenor":0.0,"roi":0.0,"gst":0.0,"nach_frequency":null,"status":"ERROR","message":"Not eligible for 45 days plan.","eligibility":null,"emiPlan":null,"navigate_url":"https://los.mihuru.com/travelplan?planId=94b858d0-8675-4feb-9522-d4013de3c5a7","qr_code":"data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAABkAQMAAABKLAcXAAAABlBMVEX///8AAABVwtN+AAAACXBIWXMAAA7EAAAOxAGVKw4bAAADuUlEQVQ4jWXT70sbdxwH8E+MzUWSJssGwyzpnZC0PtlQO2gzL503Il6ksDwVFHL+oO2gqz8K9cQsd7GgEyWxDqZQagb2D2gpjJTJkjTBU2bV7ZFM11w8wT2wmmWjns31bteHfv0+ez35fj7fN+8vwKnDZWzMcOGZmqdxTgTqazd769JoORNiKAbCbY0w75x6lIOZ8BmF8PCNHe68kq16r6/qvA+EyqAyMXRGVOjDm4PNlsEWbkC/k1v97PGPf17/1vvzrD4PsKwcbbbedtvV98ucEjdSR+JYxer/9bvHPJBfHEa+uZi3iusLYVQRWtqBlpULBGVfZAASDol5a+6BjWgvIGql/2iZaG59JAa1JYCbhpdbH3umDFuNAz4eqrO57bl9OepfdwiAyM8fuv42CDh2zO0zgG2sB46FlUgu1J/jEXVebVoi3vpfS6HYhAiBlYM+p/bLgrTcM8qD317qYi5hqsQa0qg65M16U67E7LWV+3jwPnlFzQkpozA+4BMRwYjC8idTEX/TnQAA3fgyebJVrWAT0VYeUfj4MquaRo9y7bieEuFzSLV7mKPy5SHwYAx6UsPkhkOjYxMMIl8qqyS3l3a3uFIdA4H1vx7ekKlpT2YmAohaU578eJ69k0/3sAyQ2U2y+5V5trJMZERE9ZNaRTU8c7ufuzkGelWRnJNkiUzYZX3PmgPGsWsvylfGCAYR0d5QcXwSxIVouVN/n63hp6MXFgIbsREios6TjSRfUKx1QbsmQtdTvO8kJ4/lqeJ9EYyZBqHbZNE07fw8IPJcUYAVIwuYqezR7wyt9daC1e6dvJsERITvMttd/Xkvxrk5PcHlDAXkfwR2L5YTEdGTam+/K3ME2uuUXuvhd4tFJ1+GdaABOnxqR+07edd9XOQZRESmWPinwO9K5hkPD67na7Ul15qqDHNpVGR2J+DI/na7UDPdoXeX/9Q/mM3/K9XEMVSee4X9751yKace9AFE7Bl37Nz0hQpr1HsdoTexuJAe3GtjOFR9jR/dL8pUTBTiAREYmpCTy1jM/ZR4yCDqurrjkkmlXM336M1yh3BWNk4fEIkBQk8p7lh0XpRnK3RCRNW53KK3Lv2BKwhdAFTTtdyDc0t3QU9CREQP19WL25WShzUq+p82G+fHTdaqPU11iYi4VRuJ49erXPEhPReqzSi/aTYTijCk7xIOZXzRbXlXsyUqZ+URDcSTMXgTxwDC7S/YW3kL3hL/IYmKCv7Oz11T4hLXrw/nVrMFLm/hvVFinkd06vwPlaVVuPxFT18AAAAASUVORK5CYII="},{"plan_id":"FNP","plan_name":"Fortnightly-Plan","plan_eligibility":false,"package_cost":0.0,"loan_amount":0.0,"downpayment":0.0,"processing_fee":0.0,"upfrontAmountPayDate":"0001-01-01T00:00:00","emi_amount":0.0,"emi_date":"0001-01-01T00:00:00","tenor":0.0,"roi":0.0,"gst":0.0,"nach_frequency":null,"status":"ERROR","message":"Not eligible for 15 days plan.","eligibility":null,"emiPlan":null,"navigate_url":"https://los.mihuru.com/travelplan?planId=4b51d652-450f-4486-bcc8-a81de3ec535c","qr_code":"data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAABkAQMAAABKLAcXAAAABlBMVEX///8AAABVwtN+AAAACXBIWXMAAA7EAAAOxAGVKw4bAAADuUlEQVQ4jWXT70sbdxwH8E+MzUWSJssGwyzpnZC0PtlQO2gzL503Il6ksDwVFHL+oO2gqz8K9cQsd7GgEyWxDqZQagb2D2gpjJTJkjTBU2bV7ZFM11w8wT2wmmWjns31bteHfv0+ez35fj7fN+8vwKnDZWzMcOGZmqdxTgTqazd769JoORNiKAbCbY0w75x6lIOZ8BmF8PCNHe68kq16r6/qvA+EyqAyMXRGVOjDm4PNlsEWbkC/k1v97PGPf17/1vvzrD4PsKwcbbbedtvV98ucEjdSR+JYxer/9bvHPJBfHEa+uZi3iusLYVQRWtqBlpULBGVfZAASDol5a+6BjWgvIGql/2iZaG59JAa1JYCbhpdbH3umDFuNAz4eqrO57bl9OepfdwiAyM8fuv42CDh2zO0zgG2sB46FlUgu1J/jEXVebVoi3vpfS6HYhAiBlYM+p/bLgrTcM8qD317qYi5hqsQa0qg65M16U67E7LWV+3jwPnlFzQkpozA+4BMRwYjC8idTEX/TnQAA3fgyebJVrWAT0VYeUfj4MquaRo9y7bieEuFzSLV7mKPy5SHwYAx6UsPkhkOjYxMMIl8qqyS3l3a3uFIdA4H1vx7ekKlpT2YmAohaU578eJ69k0/3sAyQ2U2y+5V5trJMZERE9ZNaRTU8c7ufuzkGelWRnJNkiUzYZX3PmgPGsWsvylfGCAYR0d5QcXwSxIVouVN/n63hp6MXFgIbsREios6TjSRfUKx1QbsmQtdTvO8kJ4/lqeJ9EYyZBqHbZNE07fw8IPJcUYAVIwuYqezR7wyt9daC1e6dvJsERITvMttd/Xkvxrk5PcHlDAXkfwR2L5YTEdGTam+/K3ME2uuUXuvhd4tFJ1+GdaABOnxqR+07edd9XOQZRESmWPinwO9K5hkPD67na7Ul15qqDHNpVGR2J+DI/na7UDPdoXeX/9Q/mM3/K9XEMVSee4X9751yKace9AFE7Bl37Nz0hQpr1HsdoTexuJAe3GtjOFR9jR/dL8pUTBTiAREYmpCTy1jM/ZR4yCDqurrjkkmlXM336M1yh3BWNk4fEIkBQk8p7lh0XpRnK3RCRNW53KK3Lv2BKwhdAFTTtdyDc0t3QU9CREQP19WL25WShzUq+p82G+fHTdaqPU11iYi4VRuJ49erXPEhPReqzSi/aTYTijCk7xIOZXzRbXlXsyUqZ+URDcSTMXgTxwDC7S/YW3kL3hL/IYmKCv7Oz11T4hLXrw/nVrMFLm/hvVFinkd06vwPlaVVuPxFT18AAAAASUVORK5CYII="},{"plan_id":"PNFL","plan_name":"Pay Now Fly Later","plan_eligibility":true,"package_cost":50000.0,"loan_amount":40000.0,"downpayment":10000.0,"processing_fee":800.0,"upfrontAmountPayDate":"2023-04-04T18:32:54.1717281+05:30","emi_amount":7367.0,"emi_date":"2023-05-05T00:00:00","tenor":6.0,"roi":1.75,"gst":144.0,"nach_frequency":"MONTHLY","status":"SUCCESS","message":null,"eligibility":"Travel","emiPlan":"Travel","navigate_url":"https://los.mihuru.com/travelplan?planId=5cb53c7c-3b84-4563-b3b6-46f5c0369591","qr_code":"data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAABkAQMAAABKLAcXAAAABlBMVEX///8AAABVwtN+AAAACXBIWXMAAA7EAAAOxAGVKw4bAAADuUlEQVQ4jWXT70sbdxwH8E+MzUWSJssGwyzpnZC0PtlQO2gzL503Il6ksDwVFHL+oO2gqz8K9cQsd7GgEyWxDqZQagb2D2gpjJTJkjTBU2bV7ZFM11w8wT2wmmWjns31bteHfv0+ez35fj7fN+8vwKnDZWzMcOGZmqdxTgTqazd769JoORNiKAbCbY0w75x6lIOZ8BmF8PCNHe68kq16r6/qvA+EyqAyMXRGVOjDm4PNlsEWbkC/k1v97PGPf17/1vvzrD4PsKwcbbbedtvV98ucEjdSR+JYxer/9bvHPJBfHEa+uZi3iusLYVQRWtqBlpULBGVfZAASDol5a+6BjWgvIGql/2iZaG59JAa1JYCbhpdbH3umDFuNAz4eqrO57bl9OepfdwiAyM8fuv42CDh2zO0zgG2sB46FlUgu1J/jEXVebVoi3vpfS6HYhAiBlYM+p/bLgrTcM8qD317qYi5hqsQa0qg65M16U67E7LWV+3jwPnlFzQkpozA+4BMRwYjC8idTEX/TnQAA3fgyebJVrWAT0VYeUfj4MquaRo9y7bieEuFzSLV7mKPy5SHwYAx6UsPkhkOjYxMMIl8qqyS3l3a3uFIdA4H1vx7ekKlpT2YmAohaU578eJ69k0/3sAyQ2U2y+5V5trJMZERE9ZNaRTU8c7ufuzkGelWRnJNkiUzYZX3PmgPGsWsvylfGCAYR0d5QcXwSxIVouVN/n63hp6MXFgIbsREios6TjSRfUKx1QbsmQtdTvO8kJ4/lqeJ9EYyZBqHbZNE07fw8IPJcUYAVIwuYqezR7wyt9daC1e6dvJsERITvMttd/Xkvxrk5PcHlDAXkfwR2L5YTEdGTam+/K3ME2uuUXuvhd4tFJ1+GdaABOnxqR+07edd9XOQZRESmWPinwO9K5hkPD67na7Ul15qqDHNpVGR2J+DI/na7UDPdoXeX/9Q/mM3/K9XEMVSee4X9751yKace9AFE7Bl37Nz0hQpr1HsdoTexuJAe3GtjOFR9jR/dL8pUTBTiAREYmpCTy1jM/ZR4yCDqurrjkkmlXM336M1yh3BWNk4fEIkBQk8p7lh0XpRnK3RCRNW53KK3Lv2BKwhdAFTTtdyDc0t3QU9CREQP19WL25WShzUq+p82G+fHTdaqPU11iYi4VRuJ49erXPEhPReqzSi/aTYTijCk7xIOZXzRbXlXsyUqZ+URDcSTMXgTxwDC7S/YW3kL3hL/IYmKCv7Oz11T4hLXrw/nVrMFLm/hvVFinkd06vwPlaVVuPxFT18AAAAASUVORK5CYII="}],"response_msg":"Congratulations your customer is approved for a credit line of Rs. 54000 valid until 04-05-2023 18:31:07, Please ask the customer to scan the QR code of respective payment plan in order to complete the Auto debit( also called eNACH) and loan agreement process"}}
/*
in case of success display assist-payment-plan
fetch from proposed plan and display the information
when user clicks on then goto nach with selected plan details
*/
